import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types';
import { useFetch, useParams } from '../../helpers/reactHooks'
import SearchBar from './SearchBar'
import Pagination from './Pagination'
import FilterList from './filters/FilterList'
import Dropdown from './Dropdown'
import ClientCard from './card-listing/ClientCard'
import ResourceCard from './card-listing/ResourceCard'

export const CARD_TYPE = {
  client: 'client',
  resource: 'resource',
  leader: 'leader',
  event: 'event'
};

const CardListing = ({
  endpoint,
  language,
  suggestionLabel,
  searchLabel,
  noResultsLabel,
  subTitle,
  resetBtnLabel,
  currentContent,
  endpointSuggestions,
  cardType,
  hasFilters,
  hasSearchBar,
  hasSortBy
}) => {
  const [initialLoad, setInitialLoad] = useState(true);

  const cardTypeName = CARD_TYPE[cardType] || CARD_TYPE.resource;

  const [
    { query, page, sort, filters },
    setQuery,
    setPage,
    setSort,
    setFilters
  ] = useParams();

  const { results, loading } = useFetch(endpoint, {
    query,
    page,
    sort,
    filters,
    currentContent,
    language
  });

  const {
    facets,
    featuredLinks,
    resultListing,
    resultsInfo,
    sortOptions,
    totalResults,
    resultsPerPage,
    resultsSummary,
    didYouMean
  } = results;

  const anchorRef = useRef();

  const handleReset = () => {
    setFilters({});
  };

  const handlePaginate = page => {
    setPage(page);
    if (anchorRef?.current) {
      anchorRef?.current.scrollIntoView();
    }
  };

  const handleFilters = filter => {
    const selectedFilters = { ...filters };
    selectedFilters[filter.name] = filter.options;

    setFilters(selectedFilters);
  };

  const handleQuery = query => {
    setQuery(query);
  };

  const getCardComponent = (item, i) => {
    switch (cardTypeName) {
      case CARD_TYPE.resource:
        return (<ResourceCard cardData={item} key={`card-${i}`}/>);
      default:
        return (<ClientCard cardData={item} cardType={cardTypeName} key={`card-${i}`}/>);
    }
  };

  useEffect(() => {
    if (Object.keys(results).length) {
      setInitialLoad(false);
    }
  }, [results])

  return (
    <div className="card-listing container">
      {!initialLoad && <>
        {hasSearchBar === 'true' && (
          <div className="card-listing__searchbar">
            {!!resultsSummary && (
              <div className="card-listing__searchbar--summary">
                <span>{resultsSummary} </span>
                {!!didYouMean && didYouMean.length > 0 && (
                  <span>
                    {suggestionLabel}
                    {didYouMean.map((keyword, index) => (
                      <button
                        onClick={() => { setQuery(keyword) }}
                        key={`suggestion-link-${index}`}
                      >
                        {keyword}
                      </button>
                    ))}
                  </span>
                )}
              </div>
            )}
            <SearchBar
              onClear={() => setQuery('')}
              onSearch={handleQuery}
              searchLabel={searchLabel}
              language={language}
              initialQuery={query && query}
              endpointSuggestions={endpointSuggestions}
            />
          </div>
        )}

        <div className="card-listing__container">
          <div className="card-listing__top">
            {featuredLinks && (
              <div className="card-listing__featured-links">
                {featuredLinks.map((link, i) => (
                  <a href={link.url} className="arrow-link" target={link.isExternal ? '_blank' : '_self'} key={`featured-link-${i}`}>
                    <span>{link.text}</span>
                    <svg viewBox={link.isExternal ? '0 0 19 18' : '0 0 18 18'}>
                      <use xlinkHref={link.isExternal ? '#link-arrow-icon' : '#link-arrow-right-icon'}/>
                    </svg>
                  </a>
                ))}
              </div>
            )}

            {hasFilters === 'true' && (
              <div className="card-listing__filters">
                {Object.keys(results).length ? (
                  <FilterList
                    filters={facets}
                    selectedFilters={filters}
                    onFiltersChange={handleFilters}
                    onResetFilters={handleReset}
                    resetBtnLabel={resetBtnLabel}
                    filterHeading={subTitle}
                  />
                ) : (
                  ''
                )}
              </div>
            )}
          </div>

          {!!resultListing && resultListing.length ? (
            <div className="card-listing__list">
              <div className="card-listing__sort-container">
                <div className="card-listing__list--title">{resultsInfo}</div>

                {hasSortBy === 'true' && (
                  <div className="card-listing__sort">
                    {sortOptions && (
                      <Dropdown
                        label="Sort by"
                        options={sortOptions}
                        selectedOption={
                          sortOptions.find(opt => opt.selected)
                            ? sortOptions.find(opt => opt.selected)
                            : sortOptions[0]
                        }
                        onChange={sort => setSort(sort.value)}
                      />
                    )}
                  </div>
                )}
              </div>

              <div ref={anchorRef} className="card-listing__list--container">
                {loading && <div className="loading"></div>}
                {!loading && resultListing.map((item, i) => getCardComponent(item, i))}
              </div>

              {!loading && !!page && (
                <div className="card-listing__list--pagination">
                  <Pagination
                    currentPage={Number(page)}
                    totalPages={Math.ceil(totalResults / resultsPerPage)}
                    onNavigate={page => handlePaginate(page)}
                    resultsPerPage={resultsPerPage}
                  />
                </div>
              )}
            </div>
          ) : (
            <div className="card-listing__list card-listing__list--no-results">
              {noResultsLabel}
            </div>
          )}
        </div>
      </>}
    </div>
  );
};

CardListing.prototype = {
  endpoint: PropTypes.string.isRequired,
  language: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  subTitle: PropTypes.string.isRequired,
  noResultsLabel: PropTypes.string.isRequired
};

export default React.memo(CardListing);
